import { SortableHeader } from '@/components/search/SortableHeader'
import './Main.css'

import React, { useEffect, useMemo, useState } from 'react'
import { useWizard } from '@/common/wizard/wizard'
import { useAppContext } from '@/context/app'
import { WizardData, checklistReportQuery, loadChecklistExcel, loadChecklistPDF } from './Model'
import { StaticPaginatedTable, StaticPaginatedTableState } from '@/components/search/StaticPaginatedTable'
import { Order, OrderManager } from '@/components/order/Order'
import { MachineName } from '@/components/machine'
import { Translates } from '@/services/repository/i18n'
import { TimeZoneNotice } from '@/components/timezone/TimeZoneNotice'
import {
  ChecklistExecution,
  ChecklistFailure,
  ChecklistMachine,
  ChecklistOperatorStat,
  ChecklistSteps,
  checklistStepState,
} from './types'

const ChecklistOrderCols = ['username', 'successCount', 'failCount', 'failures.length']
const ChecklistDefaultOrder: Order = {
  key: 'username',
  asc: true
}

const formatDateTime = (value: string) => {
  const date = new Date(value)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const operatorAnchorId = (operatorId: string) => `checklist-operator-${operatorId}`

const ChecklistMachineName = ({ machine }: { machine: ChecklistMachine }) => {
  return <MachineName row={{
    internalNumber: machine.internalNumber,
    type: {
      manufacturer: machine.manufacturer || '',
      category: machine.category || '',
      model: machine.model || '',
    }
  }} />
}

const StepIndicator = ({ execution, step, translates }: {
  execution: ChecklistExecution
  step: string
  translates: Translates
}) => {
  const state = checklistStepState(execution, step)
  if (state === 'accepted') {
    const title = translates.for('report.checklist.summary.details.accepted')
    return <span className='checklist-step checklist-step-accepted' title={title} aria-label={title}>✔</span>
  }
  if (state === 'failed') {
    const title = translates.for('report.checklist.summary.details.failed')
    return <span className='checklist-step checklist-step-failed' title={title} aria-label={title}>✘</span>
  }
  const title = translates.for('report.checklist.summary.details.not_reached')
  return <span className='checklist-step checklist-step-none' title={title} aria-label={title}>–</span>
}

const OperatorExecutions = ({ operator, translates }: {
  operator: ChecklistOperatorStat
  translates: Translates
}) => {
  const [pagginationState, setPagginationState] = useState<StaticPaginatedTableState>({
    pageNumber: 1,
    pageSize: 10
  })

  if (!operator.executions.length) {
    return <p className='checklist-empty'>{translates.for('report.checklist.summary.details.empty')}</p>
  }

  return <StaticPaginatedTable<ChecklistExecution>
    className='main-search-table checklist-executions'
    data={operator.executions}
    sortValues={{
      time: row => new Date(row.createdAt).getTime(),
      machine: row => [row.machine.manufacturer, row.machine.model, row.machine.internalNumber].filter(Boolean).join(' > '),
      status: row => translates.for(`report.checklist.summary.status.${row.status}`),
      ...Object.fromEntries(ChecklistSteps.map(step => [step, (row: ChecklistExecution) => checklistStepState(row, step)])),
    }}
    parentState={pagginationState}
    setParentState={setPagginationState}
    header={(query, onSort) => <>
      <SortableHeader field='time' query={query} onSort={onSort}>{translates.for('report.checklist.summary.details.time')}</SortableHeader>
      <SortableHeader field='machine' query={query} onSort={onSort}>{translates.for('report.checklist.summary.details.machine')}</SortableHeader>
      <SortableHeader field='status' query={query} onSort={onSort}>{translates.for('report.checklist.summary.details.status')}</SortableHeader>
      {ChecklistSteps.map(step => <SortableHeader key={step} field={step} query={query} onSort={onSort} className='hcol checklist-step-col'>
        {translates.for(`machine.checklist.wizard.${step}.title`)}
      </SortableHeader>)}
    </>}
    row={(row) => <>
      <div className='col checklist-nowrap'>{formatDateTime(row.createdAt)}</div>
      <div className='col'><ChecklistMachineName machine={row.machine} /></div>
      <div className='col'>
        <span className={`checklist-status checklist-status-${row.status}`}>
          {translates.for(`report.checklist.summary.status.${row.status}`)}
        </span>
      </div>
      {ChecklistSteps.map(step => <div key={step} className='col checklist-step-col'>
        <StepIndicator execution={row} step={step} translates={translates} />
      </div>)}
    </>}
    translates={translates}
  />
}

const OperatorFailures = ({ operator, translates }: {
  operator: ChecklistOperatorStat
  translates: Translates
}) => {
  if (!operator.failures.length) {
    return <p className='checklist-empty'>{translates.for('report.checklist.summary.failures.empty')}</p>
  }

  const reportedAt = (failure: ChecklistFailure) => {
    const date = failure.createdAt || failure.detectionDate
    return date ? formatDateTime(date) : '-'
  }

  return <ul className='checklist-failures'>
    {operator.failures.map(failure => <li key={failure.failureId} className='checklist-failure'>
      <div className='checklist-failure-head'>
        <span className='checklist-failure-category'>
          {failure.category ? translates.for(`failures.category.${failure.category}`) : '-'}
        </span>
        <span className='checklist-failure-time'>{reportedAt(failure)}</span>
      </div>
      <div className='checklist-failure-machine'><ChecklistMachineName machine={failure.machine} /></div>
      {failure.description ? <div className='checklist-failure-desc' title={failure.description}>{failure.description}</div> : null}
      <a className='checklist-failure-link' href={`/communal/machine/${failure.machine.machineId}/failures`} target='_blank' rel='noreferrer'>
        {translates.for('report.checklist.summary.failures.open')} →
      </a>
    </li>)}
  </ul>
}

export const ChecklistReportWizardResult = () => {
  const { services, session, translates } = useAppContext()
  const { wizardData } = useWizard<WizardData>()
  const [data, setData] = useState<ChecklistOperatorStat[]>()
  const [pagginationState, setPagginationState] = useState<StaticPaginatedTableState>({
    pageNumber: 1,
    pageSize: 10
  })
  const [orderManager, setOrderManager] = useState<OrderManager>(() => new OrderManager(
    ChecklistOrderCols,
    ChecklistDefaultOrder,
    (order: Order) => {
      setOrderManager(current => current.newManager(order))
      setPagginationState(current => ({ ...current, pageNumber: 1 }))
    }
  ))

  function refresh() {
    services.session.fetchWithToken(`/api/communal/my/reports/checklist?${checklistReportQuery(wizardData)}`).then((response) => {
      return response.json()
    }).then((data: ChecklistOperatorStat[]) => {
      setData(data || [])
    })
  }

  useEffect(refresh, [])

  const orderedData = useMemo(() => {
    return orderManager.orderListByKey(data || [])
  }, [data, orderManager])

  if (translates === undefined || wizardData === undefined || data === undefined) {
    return <div className='spinner'></div>
  }

  function headerRow(key: string, transKey: string) {
    if (translates === undefined) {
      return null
    }
    return <div className='hcol' onClick={() => orderManager.click(key)}>
      {translates.for('report.checklist.summary.table.' + transKey)}
      {orderManager.orderInfoElement(key)}
    </div>
  }

  const openUserStatistics = (row: ChecklistOperatorStat) => {
    window.open(`${window.location.origin}/communal/statistics/users?length=1d&user=${row.operatorId}`, '_blank')
  }

  const scrollToOperator = (row: ChecklistOperatorStat) => {
    document.getElementById(operatorAnchorId(row.operatorId))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return <>
    <div>
      <h2>{translates.for('report.checklist.summary.title')}</h2>
      <TimeZoneNotice />

      <h3>{translates.for('report.checklist.summary.operators_title')}</h3>
      <StaticPaginatedTable<ChecklistOperatorStat>
        data={orderedData.map(row => ({ ...row, id: row.operatorId }))}
        parentState={pagginationState}
        setParentState={setPagginationState}
        header={() => <>
          {headerRow('username', 'username')}
          {headerRow('successCount', 'success_count')}
          {headerRow('failCount', 'fail_count')}
          {headerRow('failures.length', 'failures_count')}
        </>}
        row={(row) => <>
          <div className='col'>
            <a className='checklist-link' onClick={() => openUserStatistics(row)}>{row.username}</a>
          </div>
          <div className='col' onClick={() => scrollToOperator(row)}>{row.successCount}</div>
          <div className='col' onClick={() => scrollToOperator(row)}>{row.failCount}</div>
          <div className='col' onClick={() => scrollToOperator(row)}>{row.failures.length}</div>
        </>}
        translates={translates}
      />

      <div className='right-btns right-btns-margin'>
        <button type="button" className='btn btn-next' onClick={() => loadChecklistExcel(services, wizardData, session?.user.lang)}>{translates.for('popup.export.excel_export')}</button>
        <button type="button" className='btn btn-next' onClick={() => loadChecklistPDF(services, wizardData, session?.user.lang)}>{translates.for('report.checklist.wizard.gen_pdf')}</button>
      </div>

      {orderedData.length ? <h3 className='checklist-details-title'>{translates.for('report.checklist.summary.details_title')}</h3> : null}
      {orderedData.map(operator => <section key={operator.operatorId} id={operatorAnchorId(operator.operatorId)} className='checklist-operator'>
        <header className='checklist-operator-header'>
          <div className='checklist-operator-identity'>
            <div>
              <a className='checklist-operator-name' onClick={() => openUserStatistics(operator)}>{operator.username}</a>
              <div className='checklist-operator-sub' onClick={() => openUserStatistics(operator)}>
                {translates.for('report.checklist.summary.user_statistics')} ↗
              </div>
            </div>
          </div>
          <div className='checklist-stats'>
            <div className='checklist-stat is-success'>
              <span className='checklist-stat-value'>{operator.successCount}</span>
              <span className='checklist-stat-label'>{translates.for('report.checklist.summary.table.success_count')}</span>
            </div>
            <div className='checklist-stat is-fail'>
              <span className='checklist-stat-value'>{operator.failCount}</span>
              <span className='checklist-stat-label'>{translates.for('report.checklist.summary.table.fail_count')}</span>
            </div>
            <div className='checklist-stat is-info'>
              <span className='checklist-stat-value'>{operator.failures.length}</span>
              <span className='checklist-stat-label'>{translates.for('report.checklist.summary.table.failures_count')}</span>
            </div>
          </div>
        </header>

        <div className='checklist-operator-body'>
          <h5 className='checklist-section-title'>
            {translates.for('report.checklist.summary.details.executions_title')}
            <span className='checklist-count-badge'>{operator.executions.length}</span>
          </h5>
          <OperatorExecutions operator={operator} translates={translates} />

          <h5 className='checklist-section-title'>
            {translates.for('report.checklist.summary.failures.title')}
            <span className='checklist-count-badge'>{operator.failures.length}</span>
          </h5>
          <OperatorFailures operator={operator} translates={translates} />
        </div>
      </section>)}
    </div>
  </>
}
